
import { useCallback } from 'react'; // Added useCallback
import { useToastStore } from "./toast-store";
import { ToastProps } from "@/components/ui/toast";
import { commonToasts } from "./toast-translations";
import type { Toast } from "./types";

// Hook for React components
export function useToast() {
  const { toasts, addToast, dismissToast, updateToast } = useToastStore();
  
  const stableToast = useCallback((props: Toast) => {
    const id = addToast(props);
    return {
      id,
      dismiss: () => dismissToast(id),
      update: (newProps: ToastProps) => updateToast(id, newProps), // Using newProps to avoid conflict
    };
  }, [addToast, dismissToast, updateToast]); // Dependencies are stable actions from Zustand

  const stableDismiss = useCallback((toastId?: string) => {
    if (toastId) {
      dismissToast(toastId);
    } else {
      // Make a copy of the array to avoid modification during iteration
      // Get current toasts directly from store if 'toasts' dependency is an issue
      const currentToasts = useToastStore.getState().toasts;
      const toastIds = [...currentToasts].map(toast => toast.id).filter(Boolean) as string[];
      toastIds.forEach(id => dismissToast(id));
    }
  }, [dismissToast]); // Dependency is a stable action from Zustand
  
  return {
    toast: stableToast,
    dismiss: stableDismiss,
    toasts, // toasts array is state and will change, consumers should be aware
  };
}

// For direct imports without hooks
export const toast = (props: Toast) => {
  const store = useToastStore.getState();
  const id = store.addToast(props);
  
  return {
    id,
    dismiss: () => useToastStore.getState().dismissToast(id),
    update: (props: ToastProps) => useToastStore.getState().updateToast(id, props),
  };
};

// Re-export for easier imports
export { commonToasts };
export type { Toast };

